import { CommonModule } from '@angular/common';
import { Component, Input, QueryList, ViewChildren } from '@angular/core';
import {
  MatExpansionModule,
  MatExpansionPanel,
} from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { RouterModule } from '@angular/router';
import { format } from 'date-fns';
import {
  ShipmentJudgedReasonType,
  SourcePage,
  SOURCE_PAGE_QUERY_PARAM,
} from '@entities/shipment';
import { MatTooltipModule } from '@angular/material/tooltip';

export type ShipmentAlertHistoryModel = {
  /** 配送ID */
  shipmentId: string;
  /** 配送名 */
  shipmentName: string;
  /** 出発予定日時 */
  startDatetime: string;
  /** 到着予定日時 */
  arrivalDatetime?: string;
  /** 配送アラート */
  status: string;
  /** 発生時刻 */
  eventAt: string;
  /** 解除時間 */
  resolvedAt?: string;
  /** 判定理由 */
  judgedReason?: ShipmentJudgedReasonType;
};

@Component({
  selector: 'app-shipment-alert-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    MatSnackBarModule,
    MatTooltipModule,
    RouterModule,
  ],
  templateUrl: './shipment-alert-detail.component.html',
  styleUrl: './shipment-alert-detail.component.scss',
})
export class ShipmentAlertDetailComponent {
  /** アラート一覧 */
  @Input({ required: true }) public alertList: ShipmentAlertHistoryModel[] = [];

  /** ローディング中か */
  @Input() public loading = false;

  @ViewChildren(MatExpansionPanel) panels = new QueryList<MatExpansionPanel>();

  private readonly judgedReasonMessageMap: {
    [key in ShipmentJudgedReasonType]: string;
  } = {
    [ShipmentJudgedReasonType.DEVICE_NOT_AT_STARTING_POINT]:
      '出発地のジオフェンス内にデバイスが無いため、準備完了にできませんでした。',
    [ShipmentJudgedReasonType.UNEXPECTED_DEVICE_STATUS]:
      'デバイスステータスが「READY」でないため、準備完了にできませんでした。',
  };

  /**
   * アラート名の取得
   * @returns アラートならTrue
   */
  public getAlertStatus(status: string): string {
    switch (status) {
      case 'NotArrived':
        return '着荷遅延';
      case 'NotStarted':
        return '出荷開始遅延';
      case 'NotReady':
        return '配送準備未完';
      default:
        return '';
    }
  }

  /**
   * アラートの解除判定
   * @param alertsPerShipment デバイス毎のアラート
   * @returns アラートならTrue
   */
  public isResolved(alertsPerShipment: ShipmentAlertHistoryModel): boolean {
    return !!alertsPerShipment.resolvedAt;
  }

  /**
   * 配送情報画面へのリンクパラメータを取得する
   *
   * @param alertHistory 配送センサーアラート履歴
   * @returns 配送情報画面へのリンクパラメータ
   */
  public getShipmentInfoLinkPrams(alertHistory: ShipmentAlertHistoryModel): {
    [key: string]: string | string[];
  } {
    const queryParams: { [key: string]: string | string[] } = {
      [SOURCE_PAGE_QUERY_PARAM]: SourcePage.DASHBOARD,
      shipmentId: alertHistory.shipmentId,
      shipmentName: alertHistory.shipmentName,
    };

    if (alertHistory.startDatetime) {
      const startDate = format(alertHistory.startDatetime, 'yyyy-MM-dd');
      queryParams['startDateRange'] = [startDate, startDate];
    }

    if (alertHistory.arrivalDatetime) {
      const arrivalDate = format(alertHistory.arrivalDatetime, 'yyyy-MM-dd');
      queryParams['arrivalDateRange'] = [arrivalDate, arrivalDate];
    }

    return queryParams;
  }

  /**
   * 判定理由表示メッセージを取得する
   * @param judgedReason 判定理由
   * @returns 表示メッセージ
   */
  public getJudgedReasonMessage(
    judgedReason: ShipmentJudgedReasonType,
  ): string {
    return this.judgedReasonMessageMap[judgedReason] || '';
  }
}
